/**
 * Channel Commands Page
 *
 * Per-channel command management: what answers in this channel, what has been
 * reworded, and what the channel owner is allowed to change. The global policy
 * over these same commands (platform defaults, the kill switch, the locks)
 * lives in Admin → Commands, not here.
 *
 * Three facts from the API shape the whole page:
 *
 *   - EVERY COMMAND IS LISTED, INCLUDING ONES THAT DO NOT ANSWER. A command
 *     switched off by its module, by this channel, or platform-wide is shown
 *     with the reason rather than hidden. Hiding it would make "why did !sr
 *     stop working" unanswerable, and would also present a taken name as a free
 *     one — the API refuses to create over it either way.
 *   - A LOCKED FIELD IS SHOWN, DISABLED, WITH THE REASON. The lock is enforced
 *     at resolution, so a value a channel stored BEFORE an admin locked the
 *     field survives and stops winning. That state has to be visible; a hidden
 *     control would leave the owner looking at text that isn't being used with
 *     no way to learn why.
 *   - EVERY WRITE RETURNS THE FULL REFRESHED ENTRY, so each save replaces its
 *     row from the response. No optimistic state, no refetch.
 *
 * NULL IS NOT "" IS NOT 0. Each overridable field has its own "Override"
 * checkbox rather than inferring intent from an empty input: unchecked sends
 * null (stop overriding), checked sends the value — including an empty string,
 * which is a real override meaning "answer nothing", and 0, which is a real
 * cooldown meaning "none". An input that rendered all three as an empty box
 * would collapse them on save.
 *
 * VARIABLE HELP (R20 chat 3). Every response field carries <VariableHelp>,
 * fed once per page from this channel's manifest (GET /api/capabilities) — the
 * route's first caller. Its load is NOT fatal: the page works without it, so a
 * failure toasts and the help offers a retry, rather than a banner.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import api, { apiErrorMessage } from '@/api/client'
import {
  channelCommandsApi,
  OVERRIDE_KEY_LABELS,
  PERMISSION_LABELS,
  PERMISSION_LEVELS,
  UNAVAILABLE_LABELS,
  type ChannelCommandEntry,
  type ChannelCommandPatch,
  type OverrideKey,
} from '@/api/commandsApi'
import { capabilitiesApi, type VariableInfo } from '@/api/capabilitiesApi'
import { useToast } from '@/components/Toast'
import VariableHelp, { VariableHelpProvider } from '@/components/VariableHelp'
import {
  AlertCircle,
  ArrowLeft,
  ChevronDown,
  ChevronRight,
  Loader2,
  Lock,
  MessageSquare,
  Plus,
  Save,
  Terminal,
  Trash2,
} from 'lucide-react'
import clsx from 'clsx'

/** Subset of GET /api/channels/:id that this page needs. */
interface ChannelHeader {
  id: number
  twitch_channel_name: string
  command_prefix: string
}

export default function ChannelCommands() {
  const { channelId: channelIdParam } = useParams<{ channelId: string }>()
  const channelId = channelIdParam ? parseInt(channelIdParam, 10) : NaN

  const [channel, setChannel] = useState<ChannelHeader | null>(null)
  const [entries, setEntries] = useState<ChannelCommandEntry[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)
  const [variables, setVariables] = useState<VariableInfo[] | null>(null)
  const [variablesError, setVariablesError] = useState<string | null>(null)

  const { success, error } = useToast()

  const prefix = channel?.command_prefix ?? '!'

  /**
   * Catalog and channel header load together, but only a failed CATALOG is
   * fatal — without it there is nothing to render. A missing header costs the
   * page its title and its prefix, so it degrades to "!" rather than to an
   * error screen.
   */
  const load = useCallback(async () => {
    if (Number.isNaN(channelId)) {
      setLoadError('Invalid channel.')
      setIsLoading(false)
      return
    }

    const [catalog, header] = await Promise.allSettled([
      channelCommandsApi.list(channelId),
      api.get<ChannelHeader>(`/api/channels/${channelId}`),
    ])

    if (catalog.status === 'fulfilled') {
      setEntries(catalog.value)
      setLoadError(null)
    } else {
      setLoadError(
        apiErrorMessage(catalog.reason, 'Could not load this channel\u2019s commands.'),
      )
    }
    if (header.status === 'fulfilled') setChannel(header.value)

    setIsLoading(false)
  }, [channelId])

  useEffect(() => {
    load()
  }, [load])

  /**
   * The variable help's data: this channel's manifest. Only `variables` is
   * used here; `capabilities` is the overlay editor's half.
   */
  const loadVariables = useCallback(async () => {
    if (Number.isNaN(channelId)) return
    setVariablesError(null)
    try {
      const manifest = await capabilitiesApi.manifest(channelId)
      setVariables(manifest.variables)
    } catch (err) {
      const message = apiErrorMessage(err, 'Could not load the variable list.')
      setVariables(null)
      setVariablesError(message)
      error(message)
    }
  }, [channelId, error])

  useEffect(() => {
    loadVariables()
  }, [loadVariables])

  const variableHelp = useMemo(
    () => ({
      variables,
      loadError: variablesError,
      retry: loadVariables,
      scoped: true,
    }),
    [variables, variablesError, loadVariables],
  )

  const replaceEntry = useCallback((updated: ChannelCommandEntry) => {
    setEntries((prev) =>
      prev.map((e) => (e.name === updated.name ? updated : e)),
    )
  }, [])

  /** Every per-command write goes through here: busy flag, row swap, toast. */
  const mutate = useCallback(
    async (
      name: string,
      call: () => Promise<ChannelCommandEntry>,
      successMessage: string,
      failureMessage: string,
    ) => {
      setBusy(name)
      try {
        replaceEntry(await call())
        success(successMessage)
      } catch (err) {
        // The row is deliberately NOT changed on failure: what's on screen
        // stays what the server actually has. The API writes a sentence for
        // every refusal it makes on purpose (locked field, not disableable,
        // handler-backed), so that sentence is what gets shown.
        error(apiErrorMessage(err, failureMessage))
      } finally {
        setBusy(null)
      }
    },
    [replaceEntry, success, error],
  )

  const handleToggle = (entry: ChannelCommandEntry) =>
    mutate(
      entry.name,
      () =>
        channelCommandsApi.patch(channelId, entry.name, {
          enabled: !entry.enabled,
        }),
      entry.enabled
        ? `${prefix}${entry.name} is off for this channel.`
        : `${prefix}${entry.name} is on for this channel.`,
      `Could not change ${prefix}${entry.name}.`,
    )

  const handleSave = (entry: ChannelCommandEntry, patch: ChannelCommandPatch) => {
    if (Object.keys(patch).length === 0) {
      success('Nothing to save \u2014 no changes.')
      return
    }
    mutate(
      entry.name,
      () => channelCommandsApi.patch(channelId, entry.name, patch),
      `Saved ${prefix}${entry.name}.`,
      `Could not save ${prefix}${entry.name}.`,
    )
  }

  const handleDelete = async (entry: ChannelCommandEntry) => {
    if (
      !confirm(
        `Delete ${prefix}${entry.name}?\n\nThis removes the command from your channel. It cannot be undone, but you can create it again.`,
      )
    ) {
      return
    }
    setBusy(entry.name)
    try {
      await channelCommandsApi.remove(channelId, entry.name)
      setEntries((prev) => prev.filter((e) => e.name !== entry.name))
      success(`Deleted ${prefix}${entry.name}.`)
    } catch (err) {
      error(apiErrorMessage(err, `Could not delete ${prefix}${entry.name}.`))
    } finally {
      setBusy(null)
    }
  }

  const handleCreate = async (
    name: string,
    responseText: string,
    permission: string,
    cooldown: number,
  ) => {
    setBusy('__new__')
    try {
      const created = await channelCommandsApi.create(channelId, {
        name,
        response_text: responseText,
        permission,
        cooldown_seconds: cooldown,
      })
      setEntries((prev) =>
        [...prev, created].sort((a, b) => a.name.localeCompare(b.name)),
      )
      setAdding(false)
      setExpanded(created.name)
      success(`Created ${prefix}${created.name}.`)
    } catch (err) {
      // 409 carries the useful sentence: the name is taken, possibly by a
      // command that isn't answering right now.
      error(apiErrorMessage(err, 'Could not create that command.'))
    } finally {
      setBusy(null)
    }
  }

  const owned = useMemo(
    () => entries.filter((e) => e.channel_owned),
    [entries],
  )
  const platform = useMemo(
    () => entries.filter((e) => !e.channel_owned),
    [entries],
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-synth-magenta" size={32} />
      </div>
    )
  }

  return (
    <VariableHelpProvider value={variableHelp}>
      <div className="space-y-6">
        <div>
          <Link
            to={`/channels/${channelId}`}
            className="inline-flex items-center gap-2 text-sm text-text-secondary hover:text-text-primary mb-2"
          >
            <ArrowLeft size={16} />
            {channel?.twitch_channel_name ?? 'Channel'}
          </Link>
          <h2 className="text-2xl font-bold text-text-primary flex items-center gap-3">
            <Terminal size={22} className="text-synth-magenta" />
            Commands
          </h2>
          <p className="text-text-secondary text-sm mt-1">
            Everything this channel can answer, on the prefix{' '}
            <span className="code-inline">{prefix}</span>. Reword what you're
            allowed to, switch off what you don't want, and add your own.
          </p>
        </div>

        {loadError && (
          <div className="bg-state-danger-bg border border-state-danger rounded-lg p-3 flex items-center gap-3">
            <AlertCircle className="text-state-danger shrink-0" size={18} />
            <span className="text-state-danger text-sm">{loadError}</span>
          </div>
        )}

        {/* Your own commands */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="label mb-0">Your commands</h3>
            {!adding && (
              <button
                className="btn btn-secondary btn-sm"
                onClick={() => setAdding(true)}
                disabled={!!loadError}
              >
                <Plus size={15} className="mr-1.5" />
                Add command
              </button>
            )}
          </div>

          {adding && (
            <NewCommandForm
              prefix={prefix}
              busy={busy === '__new__'}
              onCancel={() => setAdding(false)}
              onCreate={handleCreate}
            />
          )}

          {owned.length === 0 && !adding ? (
            <div className="card">
              <p className="text-text-muted text-sm py-4 text-center">
                You haven't added any commands yet. Yours are the only ones you
                can delete.
              </p>
            </div>
          ) : (
            owned.map((entry) => (
              <CommandCard
                key={entry.name}
                entry={entry}
                prefix={prefix}
                busy={busy === entry.name}
                isExpanded={expanded === entry.name}
                onToggleExpand={() =>
                  setExpanded((p) => (p === entry.name ? null : entry.name))
                }
                onToggleEnabled={() => handleToggle(entry)}
                onSave={(patch) => handleSave(entry, patch)}
                onDelete={() => handleDelete(entry)}
              />
            ))
          )}
        </div>

        {/* Platform commands */}
        <div className="space-y-3">
          <h3 className="label mb-0">Built-in and module commands</h3>
          {platform.length === 0 ? (
            <div className="card">
              <p className="text-text-muted text-sm py-4 text-center">
                No platform commands are defined yet.
              </p>
            </div>
          ) : (
            platform.map((entry) => (
              <CommandCard
                key={entry.name}
                entry={entry}
                prefix={prefix}
                busy={busy === entry.name}
                isExpanded={expanded === entry.name}
                onToggleExpand={() =>
                  setExpanded((p) => (p === entry.name ? null : entry.name))
                }
                onToggleEnabled={() => handleToggle(entry)}
                onSave={(patch) => handleSave(entry, patch)}
              />
            ))
          )}
        </div>

        <p className="text-text-muted text-xs leading-relaxed">
          Responses can use variables such as{' '}
          <span className="code-inline">$(user)</span> or{' '}
          <span className="code-inline">$(uptime)</span> &mdash; open{' '}
          <em>Variables</em> under any response to see them all. A command
          changed here takes effect on the next line in chat, not after a delay.
        </p>
      </div>
    </VariableHelpProvider>
  )
}

/* ------------------------------------------------------------------ */
/* Add a command                                                       */
/* ------------------------------------------------------------------ */

function NewCommandForm({
  prefix,
  busy,
  onCancel,
  onCreate,
}: {
  prefix: string
  busy: boolean
  onCancel: () => void
  onCreate: (
    name: string,
    responseText: string,
    permission: string,
    cooldown: number,
  ) => void
}) {
  const [name, setName] = useState('')
  const [responseText, setResponseText] = useState('')
  const [permission, setPermission] = useState<string>('everyone')
  const [cooldown, setCooldown] = useState('0')
  const responseRef = useRef<HTMLTextAreaElement>(null)

  const cooldownValue = Number.parseInt(cooldown, 10)
  const cooldownValid = Number.isFinite(cooldownValue) && cooldownValue >= 0
  const canSubmit = name.trim().length > 0 && responseText.length > 0 && cooldownValid

  return (
    <div className="card card-pad-sm space-y-3">
      <div className="flex items-center gap-2">
        <span className="text-text-muted">{prefix}</span>
        <input
          className="input w-auto font-mono text-sm py-1.5"
          placeholder="command name"
          value={name}
          disabled={busy}
          onChange={(e) => setName(e.target.value)}
          aria-label="New command name"
        />
      </div>

      <div>
        <label className="label" htmlFor="new-command-response">
          Response
        </label>
        <textarea
          id="new-command-response"
          ref={responseRef}
          className="textarea min-h-[4.5rem]"
          value={responseText}
          disabled={busy}
          placeholder="@$(user) hello!"
          onChange={(e) => setResponseText(e.target.value)}
        />
        <VariableHelp
          text={responseText}
          textareaRef={responseRef}
          onInsert={setResponseText}
          disabled={busy}
        />
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="label" htmlFor="new-command-permission">
            Who can use it
          </label>
          <select
            id="new-command-permission"
            className="select w-auto text-sm py-1.5"
            value={permission}
            disabled={busy}
            onChange={(e) => setPermission(e.target.value)}
          >
            {PERMISSION_LEVELS.map((level) => (
              <option key={level} value={level}>
                {PERMISSION_LABELS[level]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="new-command-cooldown">
            Cooldown (seconds)
          </label>
          <input
            id="new-command-cooldown"
            className="input w-24 text-sm py-1.5"
            type="number"
            min={0}
            value={cooldown}
            disabled={busy}
            onChange={(e) => setCooldown(e.target.value)}
          />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          className="btn btn-primary btn-sm disabled:opacity-50"
          disabled={busy || !canSubmit}
          onClick={() =>
            onCreate(name, responseText, permission, cooldownValue)
          }
        >
          {busy ? (
            <Loader2 size={15} className="mr-1.5 animate-spin" />
          ) : (
            <Plus size={15} className="mr-1.5" />
          )}
          Create
        </button>
        <button className="btn btn-ghost btn-sm" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
        {!cooldownValid && (
          <span className="error-text">Cooldown must be 0 or more.</span>
        )}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/* One command                                                         */
/* ------------------------------------------------------------------ */

function CommandCard({
  entry,
  prefix,
  busy,
  isExpanded,
  onToggleExpand,
  onToggleEnabled,
  onSave,
  onDelete,
}: {
  entry: ChannelCommandEntry
  prefix: string
  busy: boolean
  isExpanded: boolean
  onToggleExpand: () => void
  onToggleEnabled: () => void
  onSave: (patch: ChannelCommandPatch) => void
  onDelete?: () => void
}) {
  return (
    <div
      className={clsx(
        'card card-pad-sm',
        !entry.available && 'border-state-warning/40',
      )}
    >
      <div className="flex items-start gap-3">
        <button
          onClick={onToggleExpand}
          aria-expanded={isExpanded}
          className="mt-0.5 p-1 -m-1 rounded text-text-muted hover:text-text-primary shrink-0"
          aria-label={isExpanded ? `Collapse ${entry.name}` : `Expand ${entry.name}`}
        >
          {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
        </button>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="code-inline text-sm">
              {prefix}
              {entry.name}
            </span>
            <SourceBadge entry={entry} />
            {!entry.available && entry.unavailable_reason && (
              <span className="badge-danger">
                {UNAVAILABLE_LABELS[entry.unavailable_reason]}
              </span>
            )}
            {entry.overridden_keys.length > 0 && (
              <span className="badge-info">Customised</span>
            )}
            {entry.inert_keys.length > 0 && (
              <span className="badge-danger">Not in use</span>
            )}
            {entry.disable_refused && (
              <span className="badge-danger">Can't be switched off</span>
            )}
          </div>

          <p className="text-text-muted text-xs mt-1 truncate">
            {entry.handler_backed
              ? entry.description || 'Answers from code.'
              : entry.effective_response_text || '(answers nothing)'}
          </p>

          <p className="text-text-muted text-xs mt-1">
            {PERMISSION_LABELS[entry.effective_permission] ??
              entry.effective_permission}
            {entry.effective_cooldown_seconds > 0
              ? ` \u00b7 ${entry.effective_cooldown_seconds}s cooldown`
              : ' \u00b7 no cooldown'}
          </p>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {busy && (
            <Loader2 className="animate-spin text-text-secondary" size={18} />
          )}
          <button
            onClick={onToggleEnabled}
            disabled={busy || !entry.channel_disableable}
            className={clsx(
              'btn btn-sm disabled:opacity-50',
              entry.enabled ? 'btn-secondary' : 'btn-danger',
            )}
            title={
              entry.channel_disableable
                ? entry.enabled
                  ? 'Switch this off for your channel'
                  : 'Switch this back on'
                : 'The administrator does not allow this command to be switched off'
            }
          >
            {entry.enabled ? 'On' : 'Off'}
          </button>
        </div>
      </div>

      {isExpanded && (
        <div className="mt-4 pl-8">
          <div className="divider" />
          <OverrideEditor
            key={`${entry.name}:${entry.overridden_keys.join(',')}`}
            entry={entry}
            busy={busy}
            onSave={onSave}
            onDelete={onDelete}
          />
        </div>
      )}
    </div>
  )
}

function SourceBadge({ entry }: { entry: ChannelCommandEntry }) {
  if (entry.channel_owned) return <span className="badge-success">Yours</span>
  if (entry.source === 'module') {
    return (
      <span className="badge-neutral" title={entry.owner_module ?? undefined}>
        {entry.owner_module ?? 'Module'}
      </span>
    )
  }
  return <span className="badge-neutral">Built in</span>
}

/* ------------------------------------------------------------------ */
/* Overrides                                                           */
/* ------------------------------------------------------------------ */

/**
 * The three override fields, each with its own on/off.
 *
 * The checkbox is the NULL boundary and is not decoration: unchecked means "no
 * override" (null), checked means "this exact value", and the two are different
 * even when the value is empty or zero. Inferring from a blank input would make
 * "answer nothing" impossible to express and would clear a stored override
 * whenever someone emptied a box to retype it.
 *
 * A locked field is rendered DISABLED WITH ITS REASON rather than hidden. The
 * lock is enforced when a command resolves, so a value stored before the lock
 * still exists and simply stops winning — the owner has to be able to see that,
 * and to clear it, which the API permits precisely because the alternative
 * strands the row.
 */
function OverrideEditor({
  entry,
  busy,
  onSave,
  onDelete,
}: {
  entry: ChannelCommandEntry
  busy: boolean
  onSave: (patch: ChannelCommandPatch) => void
  onDelete?: () => void
}) {
  const owned = entry.channel_owned
  const isLocked = (key: OverrideKey) => entry.locked_keys.includes(key)
  const isInert = (key: OverrideKey) => entry.inert_keys.includes(key)

  // For an owned command the row IS the command, so the fields are always
  // "on" and there is nothing to override.
  const [textOn, setTextOn] = useState(
    owned || entry.override.response_text !== null,
  )
  const [text, setText] = useState(
    entry.override.response_text ?? entry.effective_response_text ?? '',
  )
  const [permOn, setPermOn] = useState(owned || entry.override.permission !== null)
  const [perm, setPerm] = useState(
    entry.override.permission ?? entry.effective_permission,
  )
  const [coolOn, setCoolOn] = useState(
    owned || entry.override.cooldown_seconds !== null,
  )
  const [cool, setCool] = useState(
    String(entry.override.cooldown_seconds ?? entry.effective_cooldown_seconds),
  )

  const coolValue = Number.parseInt(cool, 10)
  const coolValid = Number.isFinite(coolValue) && coolValue >= 0

  const responseRef = useRef<HTMLTextAreaElement>(null)
  const textDisabled = busy || isLocked('response_text') || (!owned && !textOn)

  /**
   * Only what actually changed. Built key by key on purpose: JSON.stringify
   * drops undefined and keeps null, so an untouched field must never appear in
   * the object at all, while a cleared one must appear as an explicit null.
   */
  const buildPatch = (): ChannelCommandPatch => {
    const patch: ChannelCommandPatch = {}

    if (!isLocked('response_text')) {
      const next = textOn ? text : null
      if (next !== entry.override.response_text) patch.response_text = next
    }
    if (!isLocked('permission')) {
      const next = permOn ? perm : null
      if (next !== entry.override.permission) patch.permission = next
    }
    if (!isLocked('cooldown_seconds') && coolValid) {
      const next = coolOn ? coolValue : null
      if (next !== entry.override.cooldown_seconds) patch.cooldown_seconds = next
    }
    return patch
  }

  return (
    <div className="space-y-4 pt-4">
      {entry.inert_keys.length > 0 && (
        <div className="bg-state-warning-bg border border-state-warning rounded-lg p-3 flex items-start gap-3">
          <AlertCircle className="text-state-warning shrink-0 mt-0.5" size={16} />
          <span className="text-state-warning text-sm">
            You've set{' '}
            {entry.inert_keys
              .map((k) => OVERRIDE_KEY_LABELS[k].toLowerCase())
              .join(' and ')}{' '}
            for this command, but it isn't being used
            {entry.handler_backed && entry.inert_keys.includes('response_text')
              ? ' — this command builds its answer in code.'
              : ' — the administrator has since locked it.'}{' '}
            Clear it below to tidy up.
          </span>
        </div>
      )}

      {/* Response */}
      <FieldRow
        label="Response"
        overridable={!owned}
        on={textOn}
        setOn={setTextOn}
        locked={isLocked('response_text')}
        inert={isInert('response_text')}
        lockReason={
          entry.handler_backed
            ? 'This command builds its answer in code, so there is no text to change.'
            : 'The administrator has locked the response for this command.'
        }
        defaultHint={
          owned
            ? undefined
            : entry.handler_backed
              ? 'Built in code'
              : `Default: ${entry.effective_response_text || '(nothing)'}`
        }
        busy={busy}
      >
        <textarea
          ref={responseRef}
          className="textarea min-h-[4.5rem]"
          value={text}
          disabled={textDisabled}
          onChange={(e) => setText(e.target.value)}
          aria-label={`Response for ${entry.name}`}
        />
        {!entry.handler_backed && (
          <VariableHelp
            text={text}
            textareaRef={responseRef}
            onInsert={setText}
            disabled={textDisabled}
          />
        )}
      </FieldRow>

      {/* Permission */}
      <FieldRow
        label="Who can use it"
        overridable={!owned}
        on={permOn}
        setOn={setPermOn}
        locked={isLocked('permission')}
        inert={isInert('permission')}
        lockReason="The administrator has locked who may use this command."
        defaultHint={
          owned
            ? undefined
            : `Default: ${
                PERMISSION_LABELS[entry.effective_permission] ??
                entry.effective_permission
              }`
        }
        busy={busy}
      >
        <select
          className="select w-auto text-sm py-1.5"
          value={perm}
          disabled={busy || isLocked('permission') || (!owned && !permOn)}
          onChange={(e) => setPerm(e.target.value)}
          aria-label={`Permission for ${entry.name}`}
        >
          {PERMISSION_LEVELS.map((level) => (
            <option key={level} value={level}>
              {PERMISSION_LABELS[level]}
            </option>
          ))}
        </select>
      </FieldRow>

      {/* Cooldown */}
      <FieldRow
        label="Cooldown (seconds)"
        overridable={!owned}
        on={coolOn}
        setOn={setCoolOn}
        locked={isLocked('cooldown_seconds')}
        inert={isInert('cooldown_seconds')}
        lockReason="The administrator has locked the cooldown for this command."
        defaultHint={
          owned ? undefined : `Default: ${entry.effective_cooldown_seconds}s`
        }
        busy={busy}
      >
        <input
          className="input w-24 text-sm py-1.5"
          type="number"
          min={0}
          value={cool}
          disabled={busy || isLocked('cooldown_seconds') || (!owned && !coolOn)}
          onChange={(e) => setCool(e.target.value)}
          aria-label={`Cooldown for ${entry.name}`}
        />
      </FieldRow>

      {!coolValid && <p className="error-text">Cooldown must be 0 or more.</p>}

      <div className="flex items-center gap-3">
        <button
          className="btn btn-primary btn-sm disabled:opacity-50"
          disabled={busy || !coolValid}
          onClick={() => onSave(buildPatch())}
        >
          <Save size={15} className="mr-1.5" />
          Save
        </button>

        {onDelete && (
          <button
            className="btn btn-danger btn-sm disabled:opacity-50"
            disabled={busy}
            onClick={onDelete}
          >
            <Trash2 size={15} className="mr-1.5" />
            Delete
          </button>
        )}

        {!onDelete && (
          <span className="text-text-muted text-xs flex items-center gap-1.5">
            <MessageSquare size={13} />
            Platform commands can be switched off, but not deleted.
          </span>
        )}
      </div>
    </div>
  )
}

/**
 * One labelled field, with the override switch and the lock explanation beside
 * it. Split out so the three fields cannot drift apart in how they present the
 * same three states.
 */
function FieldRow({
  label,
  overridable,
  on,
  setOn,
  locked,
  inert,
  lockReason,
  defaultHint,
  busy,
  children,
}: {
  label: string
  overridable: boolean
  on: boolean
  setOn: (value: boolean) => void
  locked: boolean
  inert: boolean
  lockReason: string
  defaultHint?: string
  busy: boolean
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="flex items-center gap-3 flex-wrap mb-1">
        <span className="label mb-0">{label}</span>

        {overridable && !locked && (
          <label className="flex items-center gap-2 text-xs text-text-secondary">
            <input
              type="checkbox"
              checked={on}
              disabled={busy}
              onChange={() => setOn(!on)}
              className="accent-synth-magenta"
            />
            Override
          </label>
        )}

        {locked && (
          <span
            className="badge-neutral gap-1.5"
            title={lockReason}
          >
            <Lock size={11} />
            Locked
          </span>
        )}

        {inert && <span className="badge-danger">Stored, not used</span>}
      </div>

      {children}

      {locked ? (
        <p className="help-text">{lockReason}</p>
      ) : overridable && !on && defaultHint ? (
        <p className="help-text">{defaultHint}</p>
      ) : null}
    </div>
  )
}
